<i18n>
{
  "en": {
    "top_movies": "Top Movies"
  },
  "ja": {
    "top_movies": "スタッフおすすめ"
  }
}
</i18n>

<template>
<div class="container">
  <div class="section">
    <h2>{{ $t('top_movies') }}</h2>

    <div class="top-pick-slider" v-if="randomBOBList.length > 0">
      <div class="slider-mask mask-left"></div>
      <div class="slider-mask mask-right"></div>
      <VueSlickCarousel v-bind="carouselSettings">
        <div v-for="movie in randomBOBList" :key="movie.MetaMovieID">
          <router-link :to="`/movies/${movie.MovieID}/`">
            <img
              :src="`/assets/sample/${movie.MovieID}/str.jpg`"
              :data-movie-id="movie.MovieID"
              :data-movie-title="movie.Title"
              :data-actress-name="movie.Actor"
              v-sfw>
          </router-link>
        </div>
      </VueSlickCarousel>
    </div>
  </div>
</div>
</template>

<script>
/* eslint max-len: 0 */

import VueSlickCarousel from 'vue-slick-carousel';
import 'vue-slick-carousel/dist/vue-slick-carousel.css';
import store from '@/store';
import MovieHelper from '@/assets/js/utils/movie';
import BifrostAPI from '@/assets/js/services/Bifrost/API';

const BOB_SIZE = 49;

export default {
  components: {
    VueSlickCarousel,
  },
  props: {
    numMovies: Number,
  },
  data() {
    return {
      randomBOBList: [],
      carouselSettings: {
        autoplay: true,
        autoplaySpeed: 5000,
        slidesToShow: 1,
        slidesToScroll: 1,
        infinite: true,
        speed: 500,
        centerMode: true,
        variableWidth: true,
        responsive: [
          {
            breakpoint: 800,
            settings: {
              centerMode: false,
              variableWidth: false,
            },
          },
        ],
      },
    };
  },
  created() {
    this.getMovies(this.userTypeID);
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    userTypeID() {
      return this.$store.getters['user/typeID'];
    },
  },
  methods: {
    async getMovies(userTypeID) {
      // randomize BOB list using a "positively skewed" normal distribution (imagine a bell curve,
      // but with the peak way earlier). so this way, the top X movies aren't always being selected,
      // and give the "lesser" BOB movies a chance to be shown and become more popular, and hence
      // (if meta's BOB algo works correctly), these now-popular movies will bubble up into the
      // beginning part of the curve and less likely to be selected, with a newer random selection
      // of BOB movies now being selected. rinse and repeat.
      //
      // borrowed the random function from an answer here:
      // https://stackoverflow.com/questions/25582882/javascript-math-random-normal-distribution-gaussian-bell-curve
      // https://jsfiddle.net/jofxbdwr/1/
      //
      // also, we are saving the randomly selected BOB movies. we're saving a random set of indexes
      // instead of random movieIDs is in case the user reloads an updated list_bob_0.json file, and
      // if we were saving movieIDs and the movieID disappeared from this JSON, things would break.
      // so we save random indexes so that if this "movies fall off the bob list" happens, doing it
      // by indexes will still work
      //
      // the reason why i'm overthinking this and not just showing random BOB movies every time is
      // that youtube's tv app has a recommmendations list that appears on their home page, and it
      // changes when you click on a video and then go back to the home page, which gives you a new
      // recommended list. so you're screwed if there's 2 videos you wanted to see on that first
      // recommended list, since it's gone by the time you get back to the home page. absolutely
      // *infuriating*!! so hopefully saving some horny dude some stress by saving this random BOB
      // list for a short amount of time (via a vuex store) in case he navigates to and from the
      // home page from this random BOB carousel
      const bfAPI = new BifrostAPI();
      const result = await bfAPI.getMovieListBOB(0, BOB_SIZE);
      const movieList = MovieHelper.expandDetailsList(result.Rows, this.locale, userTypeID);
      const movieListFiltered = MovieHelper.filterExcludedMovieList(movieList, userTypeID);

      function randnBm(min, max, skew) {
        let u = 0;
        let v = 0;
        while (u === 0) u = Math.random(); // Converting [0,1) to (0,1)
        while (v === 0) v = Math.random();
        let num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);

        num = num / 10.0 + 0.5; // Translate to 0 -> 1
        if (num > 1 || num < 0) num = randnBm(min, max, skew); // resample between 0 and 1 if out of range
        num **= skew; // Skew
        num *= max - min; // Stretch to fill range
        num += min; // offset to min
        return num;
      }

      function roundToPrecision(x, precision) {
        // from https://stackoverflow.com/a/9280882
        return Math.floor(x / precision) * precision;
      }

      const storedRands = store.getters['prefs/bobIndexes'];
      let selectedRands = []; // remember selected randoms so we don't dupe

      if (storedRands.length > 0) {
        selectedRands = storedRands;
        selectedRands.forEach((idx) => {
          this.randomBOBList.push(movieListFiltered[idx]);
        });
      } else {
        while (this.randomBOBList.length < this.numMovies) {
          const randNum = randnBm(0, BOB_SIZE, 2);
          const roundedOffset = roundToPrecision(randNum, 1);
          if (!selectedRands.includes(roundedOffset)) {
            this.randomBOBList.push(movieListFiltered[roundedOffset]);
            selectedRands.push(roundedOffset);
          }
        }
        this.$store.dispatch('prefs/setBobIndexes', selectedRands);
      }
    },
  },
  watch: {
    userTypeID(newVal) {
      this.getMovies(newVal);
    },
    numMovies(newVal, oldVal) {
      if (newVal !== oldVal) this.getMovies(this.userTypeID);
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
